import i18n from 'i18next';

/**
 * CHANGING THE UI LANGUAGE, IN ONE PLACE.
 *
 * It is four things, not one, and every surface that wrote them out for itself
 * got a different subset:
 *
 *  1. REMEMBER IT (`language`) -- the detector reads this key on the next load.
 *  2. MARK IT AS THE READER'S OWN (`languageExplicit`). A business can force a
 *     language on its storefront; that may override a device default and must
 *     never override a reader who has chosen. Losing this line is how a
 *     shopper's choice is silently undone on the next page.
 *  3. TELL i18next. This is also what turns the page around: `RtlSync` follows
 *     `i18n.language` and writes `dir` and `lang` onto the document.
 *  4. TELL THE SERVER, for a signed-in customer, so their notifications arrive
 *     in the language they just picked.
 *
 * The navbars had this written out twice already. The shop's side panel would
 * have been a third copy -- and the third copy is the one that quietly does
 * three of the four -- so it lives here and all of them call it.
 *
 * The API module is reached through a dynamic import, as the navbars reached
 * it: this util is imported by components that must not pull the whole API
 * client (and its interceptors) into their bundle just to switch a language.
 * The call is best-effort and never in the way of the language changing.
 */
export function setUiLanguage(code) {
  const next = String(code || '').trim();
  if (!next) return Promise.resolve();

  try { localStorage.setItem('language', next); } catch (e) { /* private mode */ }
  try { localStorage.setItem('languageExplicit', '1'); } catch (e) { /* private mode */ }

  i18n.changeLanguage(next);

  let token = null;
  try {
    token = localStorage.getItem('authToken') || localStorage.getItem('accessToken');
  } catch (e) { /* private mode */ }
  if (!token) return Promise.resolve();

  return import('../api/storeApi')
    .then(({ updateUserLocale }) => updateUserLocale(next, true))
    .catch(() => { /* the language has already changed; this is bookkeeping */ });
}

/**
 * The language the page is being read in right now, as a code a third party
 * (Google Places, the Geocoder) accepts -- or undefined, so the caller omits it
 * rather than sending something it cannot use.
 */
export function currentUiLanguage() {
  const code = String(i18n.language || '').split('-')[0];
  return ['en', 'ar', 'he'].includes(code) ? code : undefined;
}

export default setUiLanguage;
